class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.triggerElements = [];
    this.activeElement = null;
    this.refreshPromise = null;
    this.openFrame = null;
    this.closeTimer = null;
    this.closeTransitionTarget = null;
    this.closeTransitionHandler = null;
    this.handleKeyup = (event) => event.code === 'Escape' && this.close();
    this.handleOverlayClick = () => this.close();
    this.handleTriggerClick = (event) => {
      event.preventDefault();
      this.refreshAndOpen(event.currentTarget).catch(() => this.open(event.currentTarget));
    };
    this.handleTriggerKeydown = (event) => {
      if (event.code.toUpperCase() !== 'SPACE') return;

      event.preventDefault();
      this.refreshAndOpen(event.currentTarget).catch(() => this.open(event.currentTarget));
    };
    this.handleCartUpdated = (event) => this.updateCartCounts(event.detail?.itemCount);
    this.handleSectionLoad = () => this.setHeaderCartIconAccessibility();
    this.handleUpsellClick = (event) => {
      const button = event.target.closest('[data-ao-cart-upsell-add]');
      if (!button || !this.contains(button)) return;

      event.preventDefault();
      this.addUpsell(button);
    };

    this.addEventListener('keyup', this.handleKeyup);
    this.addEventListener('click', this.handleUpsellClick);
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.handleOverlayClick);
  }

  connectedCallback() {
    this.setAttribute('aria-hidden', 'true');
    this.setHeaderCartIconAccessibility();
    document.addEventListener('ao:cart:updated', this.handleCartUpdated);
    document.addEventListener('shopify:section:load', this.handleSectionLoad);
  }

  disconnectedCallback() {
    this.cancelPendingClose();

    if (this.openFrame !== null) {
      cancelAnimationFrame(this.openFrame);
      this.openFrame = null;
    }

    this.removeHeaderCartIconAccessibility();
    this.removeEventListener('click', this.handleUpsellClick);
    document.removeEventListener('ao:cart:updated', this.handleCartUpdated);
    document.removeEventListener('shopify:section:load', this.handleSectionLoad);
  }

  setHeaderCartIconAccessibility() {
    this.removeHeaderCartIconAccessibility();
    this.triggerElements = [...document.querySelectorAll('[data-ao-cart-trigger], #cart-icon-bubble')];

    this.triggerElements.forEach((cartLink) => {
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.setAttribute('aria-controls', 'CartDrawer');
      cartLink.setAttribute('aria-expanded', this.classList.contains('active') ? 'true' : 'false');
      cartLink.addEventListener('click', this.handleTriggerClick);
      cartLink.addEventListener('keydown', this.handleTriggerKeydown);
    });
  }

  removeHeaderCartIconAccessibility() {
    this.triggerElements.forEach((cartLink) => {
      cartLink.removeEventListener('click', this.handleTriggerClick);
      cartLink.removeEventListener('keydown', this.handleTriggerKeydown);
    });
    this.triggerElements = [];
  }

  updateCartCounts(itemCount) {
    if (!Number.isFinite(Number(itemCount))) return;

    document.querySelectorAll('.ao-head-cart-count').forEach((count) => {
      count.textContent = String(itemCount);
      count.hidden = Number(itemCount) <= 0;
    });
  }

  async syncCartCounts() {
    try {
      const cartUrl = `${window.routes?.cart_url || '/cart'}.js`;
      const response = await fetch(cartUrl, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;

      const cart = await response.json();
      this.updateCartCounts(cart.item_count);
      document.dispatchEvent(new CustomEvent('ao:cart:updated', { detail: { itemCount: cart.item_count, cart } }));
    } catch (error) {
      // Keep the server-rendered count when the cart endpoint is temporarily unavailable.
    }
  }

  async addUpsell(button) {
    if (button.disabled || button.getAttribute('aria-busy') === 'true') return;

    const variantId = Number(button.dataset.variantId);
    const discountCode = button.dataset.discountCode?.trim();
    const originalLabel = button.textContent.trim();
    const status = button.parentElement?.querySelector('.ao-cart-drawer__recommendation-status');

    if (!Number.isFinite(variantId) || variantId <= 0 || !discountCode) return;

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.textContent = button.dataset.addingLabel || originalLabel;
    if (status) status.textContent = '';

    try {
      const addResponse = await fetch(routes.cart_add_url, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity: 1,
              properties: {
                _ao_discount_code: discountCode,
              },
            },
          ],
        }),
      });

      const addResult = await addResponse.json();
      if (!addResponse.ok) throw new Error(addResult.description || 'Cart add failed');

      const rootUrl = window.Shopify?.routes?.root || '/';
      const discountUrl = `${rootUrl}discount/${encodeURIComponent(discountCode)}?redirect=${encodeURIComponent(
        `${rootUrl}cart`,
      )}`;

      await fetch(discountUrl, {
        credentials: 'same-origin',
        redirect: 'follow',
      });

      const cartResponse = await fetch(`${routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
      });
      if (!cartResponse.ok) throw new Error('Cart refresh failed');

      const cart = await cartResponse.json();
      await this.refreshAndOpen();
      this.updateCartCounts(cart.item_count);
      document.dispatchEvent(new CustomEvent('ao:cart:updated', { detail: { itemCount: cart.item_count, cart } }));

      const liveRegion = this.querySelector('#CartDrawer-LiveRegionText');
      if (liveRegion) liveRegion.textContent = button.dataset.successLabel || '';
    } catch (error) {
      if (status) status.textContent = button.dataset.errorLabel || 'Could not add this product.';
      button.disabled = false;
      button.removeAttribute('aria-busy');
      button.textContent = originalLabel;
    }
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) {
      if (triggeredBy) this.setActiveElement(triggeredBy);
      return;
    }

    this.cancelPendingClose();
    this.classList.remove('is-closing');

    document.dispatchEvent(new CustomEvent('ao:cart:opening'));

    if (triggeredBy) {
      const visibleHeaderTrigger = triggeredBy.closest('[data-ao2-drawer]')
        ? this.triggerElements.find((trigger) => !trigger.closest('[data-ao2-drawer]'))
        : triggeredBy;

      this.setActiveElement(visibleHeaderTrigger || triggeredBy);
    }
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    this.classList.add('animate');
    this.setAttribute('aria-hidden', 'false');
    this.triggerElements.forEach((cartLink) => cartLink.setAttribute('aria-expanded', 'true'));

    if (this.openFrame !== null) cancelAnimationFrame(this.openFrame);
    this.openFrame = requestAnimationFrame(() => {
      this.openFrame = null;
      this.classList.add('active');
    });

    let focusTrapped = false;
    const trapDrawerFocus = () => {
      if (focusTrapped || !this.classList.contains('active')) return;

      focusTrapped = true;
      const containerToTrapFocusOn = this.classList.contains('is-empty')
        ? this.querySelector('.drawer__inner-empty')
        : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');

      if (containerToTrapFocusOn && focusElement) {
        trapFocus(containerToTrapFocusOn, focusElement);
      }
    };

    this.addEventListener('transitionend', trapDrawerFocus, { once: true });
    window.setTimeout(trapDrawerFocus, 340);
    document.body.classList.add('overflow-hidden');
  }

  close() {
    if (!this.classList.contains('active') && !this.classList.contains('is-closing')) return;

    if (this.openFrame !== null) {
      cancelAnimationFrame(this.openFrame);
      this.openFrame = null;
    }

    if (this.classList.contains('is-closing')) return;

    this.classList.remove('active');
    this.classList.add('is-closing');
    this.triggerElements.forEach((cartLink) => cartLink.setAttribute('aria-expanded', 'false'));

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.finishClose();
      return;
    }

    const inner = this.querySelector('.drawer__inner');

    if (!inner) {
      this.finishClose();
      return;
    }

    this.closeTransitionTarget = inner;
    this.closeTransitionHandler = (event) => {
      if (event.target !== inner || event.propertyName !== 'transform') return;

      this.finishClose();
    };
    inner.addEventListener('transitionend', this.closeTransitionHandler);
    this.closeTimer = window.setTimeout(() => this.finishClose(), 500);
  }

  finishClose() {
    this.cancelPendingClose();
    this.classList.remove('is-closing');
    this.setAttribute('aria-hidden', 'true');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  cancelPendingClose() {
    if (this.closeTimer !== null) {
      window.clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }

    if (this.closeTransitionTarget && this.closeTransitionHandler) {
      this.closeTransitionTarget.removeEventListener('transitionend', this.closeTransitionHandler);
    }

    this.closeTransitionTarget = null;
    this.closeTransitionHandler = null;
  }

  async refreshAndOpen(triggeredBy) {
    if (this.refreshPromise) {
      await this.refreshPromise;
      if (!this.classList.contains('active')) this.open(triggeredBy);
      return;
    }

    const sectionId = this.dataset.sectionId || 'cart-drawer';
    this.setAttribute('aria-busy', 'true');

    try {
      this.refreshPromise = fetch(`${routes.cart_url}?section_id=${encodeURIComponent(sectionId)}`, {
        headers: { Accept: 'text/html' },
      }).then(async (response) => {
        if (!response.ok) throw new Error(`Cart drawer refresh failed with status ${response.status}`);

        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const sourceDrawer = html.querySelector('cart-drawer');
        const sourceInner = sourceDrawer?.querySelector('.drawer__inner');
        const currentInner = this.querySelector('.drawer__inner');

        if (!sourceDrawer || !sourceInner || !currentInner) {
          throw new Error('Cart drawer markup was not returned');
        }

        currentInner.replaceWith(sourceInner);
        this.classList.toggle('is-empty', sourceDrawer.classList.contains('is-empty'));
      });

      await this.refreshPromise;
    } finally {
      this.refreshPromise = null;
      this.removeAttribute('aria-busy');
    }

    this.open(triggeredBy);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner')?.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      const sectionHtml = parsedState.sections?.[section.id];

      if (!sectionElement || !sectionHtml) return;

      sectionElement.innerHTML = this.getSectionInnerHTML(sectionHtml, section.selector);
    });

    this.classList.remove('is-empty');
    if (Number.isFinite(Number(parsedState.item_count))) {
      this.updateCartCounts(parsedState.item_count);
      document.dispatchEvent(
        new CustomEvent('ao:cart:updated', {
          detail: { itemCount: parsedState.item_count, cart: parsedState },
        }),
      );
    } else {
      this.syncCartCounts();
    }

    this.open();
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML || '';
  }

  getSectionsToRender() {
    return [
      {
        id: this.dataset.sectionId || 'cart-drawer',
        selector: '#CartDrawer',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    const sectionId = this.closest('cart-drawer')?.dataset.sectionId || 'cart-drawer';

    return [
      {
        id: 'CartDrawer',
        section: sectionId,
        selector: '.drawer__inner',
      },
    ];
  }
}

if (!customElements.get('cart-drawer-items')) {
  customElements.define('cart-drawer-items', CartDrawerItems);
}
